import { BuildUri } from "./BuildUri";

import { EventSchedule, ShiftSpot, ShiftRecurrence, Shift } from "../model/EventSchedule";

import { NetworkManager } from "../context";
import { SimpleIdentity, QuickProvisioningIdentity } from "../model/Identity";
import { ShiftRequest, PreProcessShiftRequest } from "../requests/ShiftRequest";
import { IdentityAssignmentRequest } from "../requests/IdentityAssignmentRequest";
import { ShiftSpotRequest } from "../requests/ShiftSpotRequest";
import { IdentityAssignmentMultipleRequest } from "../requests/IdentityAssignmentMultipleRequest";
import { PunchMultipleRequest } from "../requests/PunchMultipleRequest";
import { PunchMultipleAsSpecifiedRequest } from "../requests/PunchMultipleAsSpecifiedRequest";

class ScheduleEndpoints {

    getEventSchedule(eventId: number, includePunchClockState: boolean = true, fromDate?: string, toDate?: string, signal?: AbortSignal): Promise<EventSchedule> {
        return NetworkManager.fetch(
            BuildUri("/event/{eventId}", { eventId }, { includeSchedule: true, includePunchClockState, fromDate, toDate }),
            signal ? { signal } : undefined
        );
    }

    // Fetch a single shift with ALL of its recurrences (no date filtering),
    // so editing from a date-filtered (week) view doesn't drop other weeks' dates.
    getShift(shiftId: number): Promise<Shift> {
        return NetworkManager.fetch(
            BuildUri("/event/shift/{shiftId}", { shiftId })
        );
    }

    getShiftSpot(shiftSpotId: number, includePunchClock: boolean = true): Promise<ShiftSpot> {
        return NetworkManager.fetch(
            BuildUri("/event/shift/recurrence/spot/{shiftSpotId}", { shiftSpotId }, { includePunchClock: true })
        );
    }

    getSchedulableForEvent(eventId: number): Promise<Array<QuickProvisioningIdentity>> {
        return NetworkManager.fetch(
            BuildUri("/event/{eventId}/schedulable", { eventId })
        );
    }

    createShift(eventId: number, request: ShiftRequest): Promise<EventSchedule> {
        return NetworkManager.fetch(
            BuildUri("/event/{eventId}/shift", { eventId }),
            {
                method: "POST",
                body: PreProcessShiftRequest(request)
            }
        );
    }

    editShift(shiftId: number, request: ShiftRequest): Promise<EventSchedule> {
        return NetworkManager.fetch(
            BuildUri("/event/shift/{shiftId}", { shiftId }),
            {
                method: "PUT",
                body: PreProcessShiftRequest(request)
            }
        );
    }

    deleteShift(shiftId: number): Promise<EventSchedule> {
        return NetworkManager.fetch(
            BuildUri("/event/shift/{shiftId}", { shiftId }),
            {
                method: "DELETE"
            }
        );
    }

    editShiftSpotDetails(spotId: number, request: ShiftSpotRequest): Promise<ShiftSpot> {
        return NetworkManager.fetch(
            BuildUri("/event/shift/recurrence/spot/{spotId}", { spotId }),
            {
                method: "PUT",
                body: request
            }
        );
    }

    assignIdentityToSpot(spotId: number, request: IdentityAssignmentRequest): Promise<ShiftRecurrence> {
        return NetworkManager.fetch(
            BuildUri("/event/shift/recurrence/spot/{spotId}", { spotId }),
            {
                method: "POST",
                body: request
            }
        );
    }

    assignIdentityToMultipleSpots(request: IdentityAssignmentMultipleRequest): Promise<void> {
        return NetworkManager.fetch(
            BuildUri("/event/shift/recurrence/spot/assign-multiple"),
            {
                method: "POST",
                body: request
            }
        );
    }

    punchForSpotsAsScheduled(request: PunchMultipleRequest): Promise<EventSchedule> {
        return NetworkManager.fetch(
            BuildUri("/event/shift/recurrence/spot/punch-multiple"),
            {
                method: "POST",
                body: request
            }
        );
    }

    punchForSpotsAsSpecified(request: PunchMultipleAsSpecifiedRequest): Promise<EventSchedule> {
        return NetworkManager.fetch(
            BuildUri("/event/shift/recurrence/spot/punch-specified"),
            {
                method: "POST",
                body: request
            }
        );
    }

    unassignSpot(spotId: number): Promise<ShiftRecurrence> {
        return NetworkManager.fetch(
            BuildUri("/event/shift/recurrence/spot/{spotId}", { spotId }),
            {
                method: "DELETE"
            }
        );
    }
}

export default new ScheduleEndpoints();